import { DEFAULT_AXIS_LABEL_FONT_SIZE } from "constants/chart.constants";
import { EvaChart, TimeStep } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption, DEFAULT_X_AXIS_OPTIONS } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";
import { uid } from "utils";

import { AXIS_NAME_TEXT_STYLE } from "components/multiphase-chart/constants/shared.constants";

import { createChartLocks } from "../locks";
import { mpcYAxisLocks } from "../locks/mpcLocks";
import { DEFAULT_Z_INDEX, STATISTICS_Z_INDEX } from "./constants";
import {
  buildProductStyleMap,
  buildYAxisIndexRemap,
  getLineStyleForSeries,
  getYAxisPosition,
  inferAxisProduct
} from "./mpcTranslatorHelpers";
import { applyProductLegend } from "./mpcProductLegend";
import { getEffectiveSeriesColor } from "./getEffectiveSeriesColor";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  createLogAxisLabelFormatter,
  formatChartTitleWithSubscript,
  getDefaultXAxisLogOptions,
  getDefaultYAxisLogOptions,
  getLineSeriesMinMax,
  isStatisticsLabel,
  setXAxisParams
} from "./utils";

export function translateRateTimeResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true,
    product,
    entityKind,
    multiProduct,
    getSeriesStyle
  } = states;

  if (series.length === 0 && typeWellSeries.length === 0) {
    return {};
  }

  const primaryProducts = multiProduct?.properties?.primaryProducts || [];
  const secondaryProducts = multiProduct?.properties?.secondaryProducts || [];
  const allProducts = [...new Set([...primaryProducts, ...secondaryProducts])];

  const productStyleMap = buildProductStyleMap(multiProduct);

  // Calculate x min/max from ALL series — the x-axis is shared across all series
  // regardless of which y-axis they belong to (e.g. ratio series on secondary y-axis
  // can have a much longer forecast than primary series, which must be reflected in
  // the axis scale calculation to avoid month-interval ticks on a year-scale chart).
  const { xMin: allSeriesXMin, xMax: allSeriesXMax } = getLineSeriesMinMax(
    series,
    typeWellSeries
  );

  // Calculate min/max for default axis (axis index 0) — y-axis purposes only
  const defaultAxisSeries = series.filter((s) => (s.yAxisIndex ?? 0) === 0);
  const { xMin, xMax, yMin, yMax } =
    defaultAxisSeries.length > 0
      ? getLineSeriesMinMax(defaultAxisSeries, typeWellSeries)
      : getLineSeriesMinMax(series, typeWellSeries);

  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedYMinType,
    lockedYMaxType
  } = createChartLocks({
    type: EvaChart.RateTime,
    states
  });
  // Use the full x-extent across all series for axis scale computation so that
  // secondary-axis series (e.g. GOR forecast) don't cause month-interval ticks
  // when the chart range spans years.
  const currentXMaxValue = Math.floor(lockedXMax ?? allSeriesXMax.value);
  const currentXMinValue = Math.floor(lockedXMin ?? allSeriesXMin.value);

  const isXLogAxisOn = logAxis?.properties?.x?.active;
  const isYLogAxisOn = logAxis?.properties?.y?.active;

  const isDailyData = layout?.xAxis?.title.includes(TimeStep.Day);

  const { xAxisTitle, interval, formatter } = setXAxisParams(
    isDailyData,
    isXLogAxisOn,
    layout.xAxis.title,
    currentXMaxValue,
    currentXMinValue
  );
  const xAxis: Partial<XAXisOption> = {
    name: formatChartTitleWithSubscript(xAxisTitle),
    nameGap: 27,
    position: "bottom",
    min: lockedXMin,
    max: lockedXMax,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    lock: {
      show: true,
      min: {
        value: Math.floor(xMin.value),
        type: xMin.type
      },
      max: {
        value: Math.floor(xMax.value),
        type: xMax.type
      }
    }
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    lock: {
      show: true,
      min: {
        value: Math.floor(yMin.value),
        type: lockedYMinType ?? yMin.type,
        override: true
      },
      max: {
        value: Math.floor(yMax.value),
        type: lockedYMaxType ?? yMax.type,
        override: true
      }
    }
  };

  const multiYAxis =
    multiProduct?.active && layout?.yAxis2?.length
      ? layout.yAxis2.map((axis, i) => {
          const seriesForThisAxis = series.filter((s) => (s.yAxisIndex ?? 0) === i);
          const axisProduct = inferAxisProduct(allProducts, seriesForThisAxis, product);
          const axisSeriesMinMax =
            seriesForThisAxis.length > 0
              ? getLineSeriesMinMax(seriesForThisAxis, [])
              : { yMin, yMax };

          const {
            lockedYMin: axisLockedYMin,
            lockedYMax: axisLockedYMax,
            lockedYMinType: axisLockedYMinType,
            lockedYMaxType: axisLockedYMaxType
          } = mpcYAxisLocks(axisProduct, seriesForThisAxis, states);

          const axisPosition = getYAxisPosition(
            primaryProducts,
            secondaryProducts,
            seriesForThisAxis
          );

          return {
            id: i,
            type: isYLogAxisOn ? "log" : "value",
            name: formatChartTitleWithSubscript(axis.title),
            nameLocation: "middle",
            nameGap: 30,
            nameTextStyle: AXIS_NAME_TEXT_STYLE,
            axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE } as Record<
              string,
              unknown
            >,
            position: axisPosition,
            offset: 0,
            min: axisLockedYMin,
            max: axisLockedYMax,
            axisLine: { show: true, onZero: false },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            lock: {
              show: true,
              productKey: axisProduct,
              min: {
                value: Math.floor(
                  axisLockedYMin ?? axisSeriesMinMax.yMin?.value ?? yMin.value
                ),
                type: axisLockedYMinType ?? axisSeriesMinMax.yMin?.type ?? yMin.type,
                override: true
              },
              max: {
                value: Math.floor(
                  axisLockedYMax ?? axisSeriesMinMax.yMax?.value ?? yMax.value
                ),
                type: axisLockedYMaxType ?? axisSeriesMinMax.yMax?.type ?? yMax.type,
                override: true
              }
            }
          };
        })
      : null;

  const yAxisIndexRemap = new Map<number, number>();
  let limitedMultiYAxis: typeof multiYAxis = null;
  if (multiYAxis) {
    const { filteredAxes, remap } = buildYAxisIndexRemap(multiYAxis, series);
    remap.forEach((v, k) => yAxisIndexRemap.set(k, v));
    limitedMultiYAxis = filteredAxes;
  }

  if (isXLogAxisOn) {
    const xAxisLogOptions = getDefaultXAxisLogOptions(lockedXMin, lockedXMax);
    Object.assign(xAxis, xAxisLogOptions);
  }

  if (isYLogAxisOn) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.RateTime,
      product,
      entityKind
    );
    Object.assign(yAxis, yAxisLogOptions);

    if (limitedMultiYAxis) {
      limitedMultiYAxis.forEach((axis) => {
        const perAxisLogOptions = getDefaultYAxisLogOptions(
          layout,
          axis.min,
          axis.max,
          EvaChart.RateTime,
          product,
          entityKind
        );
        Object.assign(axis, perAxisLogOptions);
        axis.axisLabel = {
          ...axis.axisLabel,
          hideOverlap: true,
          formatter: createLogAxisLabelFormatter(axis.min, axis.max)
        };
      });
    }
  }

  let options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.RateTime,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: (limitedMultiYAxis as YAXisOption[]) ?? {
      ...createEntityState(ChartOption.YAxis, yAxis)
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        ...xAxis,
        minInterval: isXLogAxisOn ? undefined : interval,
        maxInterval: isXLogAxisOn ? undefined : interval,
        splitLine: isXLogAxisOn
          ? DEFAULT_X_AXIS_OPTIONS.splitLine
          : {
              ...DEFAULT_X_AXIS_OPTIONS.splitLine,
              show: true,
              interval: interval - 1
            },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false,
        axisLabel: {
          ...DEFAULT_X_AXIS_OPTIONS.axisLabel,
          hideOverlap: true,
          formatter: isXLogAxisOn
            ? (value: number) => {
                return value.toString();
              }
            : formatter
        }
      })
    },
    grid: { top: 46, bottom: allProducts.length > 0 ? 70 : 46, left: 62, right: 20 },
    ...(multiProduct?.active && {
      dataZoom: [
        { type: "inside", orient: "horizontal", filterMode: "none" },
        ...(!isYLogAxisOn
          ? [{ type: "inside", orient: "vertical", filterMode: "none" } as const]
          : [])
      ]
    }),
    ...(overrideSeries && {
      series: series
        .filter((s) => (s.xAxisIndex ?? 0) !== -1 && (s.yAxisIndex ?? 0) !== -1)
        .map((location, index) => {
          const { label, style, x, y, groupTitle } = location;

          const data = _zip(x, y);
          const id =
            groupTitle !== label
              ? `${groupTitle}, ${label}, ${index}, ${uid()}`
              : `${groupTitle}, ${index}, ${uid()}`;

          const isPoint = data.length === 1;
          const multiPoint = data.length > 1;
          const isStatisticsSeries = isStatisticsLabel(label);

          const { thickness, opacity } = getSeriesStyle(label, style);

          let lineType: string | number[];
          if (location.isForecast) {
            lineType = location.isComparisonForecast ? "dotted" : "dashed";
          } else {
            lineType = getLineStyleForSeries(productStyleMap, location, false, false);
          }

          const effectiveColor = getEffectiveSeriesColor(location, multiProduct);

          const lineSeries = createEntityState(ChartOption.LineSeries, {
            id,
            name: label,
            data: data,
            itemStyle: {
              color: effectiveColor
            },
            yAxisIndex: limitedMultiYAxis
              ? yAxisIndexRemap.get(location.yAxisIndex ?? 0) ?? 0
              : location.yAxisIndex,
            lineStyle: {
              width: thickness,
              color: effectiveColor,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              type: lineType as any,
              opacity: opacity
            },
            z: isStatisticsSeries ? STATISTICS_Z_INDEX : DEFAULT_Z_INDEX,
            ...(isPoint ? { symbolSize: 5 } : {}),
            ...(isPoint ? { symbol: "circle" } : {}),
            showSymbol: !multiPoint || isPoint
          });

          return lineSeries;
        })
    })
  });

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }

  options = applyProductLegend(options, multiProduct);

  return options;
}
